import { useMutation, useQueryClient } from '@tanstack/react-query'
import { initiateNangoAuth, isNangoAuthToastedError } from 'v2/pages/settings/integrations/nango'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import { externalService } from 'v2/shared/services/external-apps-service'
import type { IntegrationSourceGateway } from 'v2/shared/services/integration-source-gateway'
import { externalAppsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RCustomMcpServer, RExternalAppDetails, RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { generateToastsForMutation } from 'v2/utils/toasts'
import { type CustomMcpFormValues, toCustomMcpPayload } from './apps/custom-mcp-form.config'
import type { IntegrationFormValues } from './apps/integration-connection-form.config'
import { prepareConnectionDetails } from './apps/integration-form-utils'
import { invalidateExternalAppQueries } from './connection-mutations'
import type { OAuthProviderCredentials } from './oauth-provider-row.config'
import { getSafeIntegrationErrorMetadata } from './utils/safe-error-metadata'

type IntegrationConnectResult =
  | { kind: 'redirect'; url: string }
  | { kind: 'connected'; source: 'aci' | 'nango'; appName: string }

interface IntegrationConnectVariables {
  app: RExternalAppDetails
  values: IntegrationFormValues
}

export const useConnectIntegrationMutation = (
  gatewayOverride?: IntegrationSourceGateway,
  aciEnabledOverride?: boolean,
) => {
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  const aciEnabled = aciEnabledOverride ?? rollout.useAciIntegrations
  const toastOptions = generateToastsForMutation<
    IntegrationConnectResult,
    IntegrationConnectVariables
  >(
    {
      title: 'Connected',
      description: 'The integration connected successfully.',
    },
    {
      title: 'Integration Error',
      description: aciEnabled
        ? 'Could not start authentication via the bridge.'
        : 'Failed to save connection details.',
      callback: (error) =>
        logger.error('Integration connection failed', getSafeIntegrationErrorMetadata(error)),
    },
  )
  const notifySuccess = toastOptions.onSuccess as
    | ((result: IntegrationConnectResult) => void)
    | undefined

  return useMutation({
    mutationFn: async ({
      app,
      values,
    }: IntegrationConnectVariables): Promise<IntegrationConnectResult> => {
      if (!rollout.isResolved) throw new Error('Integration rollout is still loading')
      const { connectionDetails, otherDetails } = prepareConnectionDetails(values, app, aciEnabled)

      if (aciEnabled) {
        const connectionConfig = { ...connectionDetails.connection, ...otherDetails }
        const response = await gateway.connectAci(
          app.provider,
          `${window.location.origin}/integrations`,
          {
            connection_config: Object.keys(connectionConfig).length ? connectionConfig : undefined,
            api_key: connectionDetails.credentials.api_key
              ? String(connectionDetails.credentials.api_key)
              : undefined,
            username: connectionDetails.credentials.username
              ? String(connectionDetails.credentials.username)
              : undefined,
            password:
              connectionDetails.credentials.password === undefined
                ? undefined
                : String(connectionDetails.credentials.password),
          },
        )
        if (response.data.consent_url) {
          return { kind: 'redirect', url: response.data.consent_url }
        }
        if (response.data.linked_account) {
          return { kind: 'connected', source: 'aci', appName: app.name }
        }
        throw new Error('The integrations service returned no actionable result')
      }

      const response = await gateway.createLegacyConnection({
        app_uuid: app.id,
        connection_details: { ...connectionDetails, ...otherDetails },
        enabled: true,
        priority_order: app.priority,
      })
      if (!response.data.connect_session_token) {
        throw new Error('Legacy integration connect returned no session token')
      }
      await initiateNangoAuth({
        provider: app.provider,
        connection: connectionDetails.connection,
        credentials: connectionDetails.credentials,
        sessionToken: response.data.connect_session_token,
        appName: app.name,
      })
      return { kind: 'connected', source: 'nango', appName: app.name }
    },
    ...toastOptions,
    onSuccess: (result) => {
      invalidateExternalAppQueries()
      if (result.kind === 'connected' && result.source === 'aci') {
        notifySuccess?.(result)
      }
    },
    onError: (error, variables, context) => {
      // Legacy path parity with main: a failed Nango popup already toasted its
      // own "Failed to authenticate with {app}" inside initiateNangoAuth, and
      // main's caller only logged after that. Toasting again here would show
      // the user two error toasts for one failure.
      if (isNangoAuthToastedError(error)) {
        logger.error('Integration connection failed', getSafeIntegrationErrorMetadata(error))
        return
      }
      toastOptions.onError?.(error, variables, context)
    },
  })
}

export const useRegisterCustomMcpMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  const toastOptions = generateToastsForMutation<RCustomMcpServer, CustomMcpFormValues>(
    {
      title: 'MCP server connected',
      description: 'The MCP server and its tools are now available.',
      callback: () => invalidateExternalAppQueries(client),
    },
    {
      title: 'Connection failed',
      description:
        'Could not connect to the MCP server. Check the URL and credentials, then try again.',
      callback: (error) =>
        logger.error('Custom MCP registration failed', getSafeIntegrationErrorMetadata(error)),
    },
  )

  return useMutation({
    mutationFn: async (values: CustomMcpFormValues) => {
      if (!rollout.isResolved) throw new Error('Integration rollout is still loading')
      const response = await gateway.registerCustomMcp(toCustomMcpPayload(values))
      if (!response.data?.id) throw new Error('Custom MCP registration returned no server id')
      return response.data
    },
    ...toastOptions,
  })
}

export const useSetOAuthProviderCredentialsMutation = () => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const toastOptions = generateToastsForMutation<
    unknown,
    { provider: string; credentials: OAuthProviderCredentials }
  >(
    {
      title: 'Saved',
      description: 'OAuth provider credentials were saved.',
      callback: () =>
        client.invalidateQueries({ queryKey: externalAppsQueryKeys().oauthProvidersBase }),
    },
    { title: 'Error', description: 'Failed to save credentials.' },
  )
  return useMutation({
    mutationFn: ({
      provider,
      credentials,
    }: {
      provider: string
      credentials: OAuthProviderCredentials
    }) => {
      if (!rollout.isResolved) throw new Error('Integration rollout is still loading')
      return rollout.gateway.setOAuthCredentials(provider, {
        client_id: credentials.clientId,
        client_secret: credentials.clientSecret,
      })
    },
    ...toastOptions,
  })
}

export const useConfirmAciConnectionMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async (ownerId: string): Promise<RExternalOrgApp | undefined> => {
      if (!rollout.isResolved) throw new Error('Integration rollout is still loading')
      const response = await gateway.getAciConnections()
      return response.data.find((connection) => connection.id === ownerId)
    },
    // This runs on the OAuth landing page, the moment a brand-new connection
    // comes back from the provider. Without invalidation the user is told
    // "Connected" and then lands on an integrations list — and a flow's app
    // picker — that was cached before the connection existed.
    onSuccess: () => invalidateExternalAppQueries(client),
  })
}

export const useFinishLegacyAciConnectMutation = () =>
  useMutation({
    mutationFn: (orgAppUuid: string) => externalService.finishLegacyAciConnect(orgAppUuid),
    // Deliberately no toast: the only consumer (aci-oauth-landing) awaits this with
    // mutateAsync and raises its own "Could not finalize the connection" toast, so a
    // second one here would double up on the landing screen. The logger is
    // unconditional, though — the response interceptor swallows 4xx without
    // reporting, so without this a failure reaching any FUTURE consumer would be
    // silent on our side even when the user sees something.
    onError: (error) =>
      logger.error('ACI legacy finish-connect failed', getSafeIntegrationErrorMetadata(error)),
  })
